import { useState, useEffect, useCallback } from 'react';
import * as analyticsApi from 'features/analytics/studio/api/analyticsApi';
import { getStoredUserId, userCanViewBoard } from 'features/analytics/studio/utils/boardAccess';
import { trackClientEvent } from 'shared/utils/trodoEvents';

/**
 * Custom hook for managing boards
 */
export const useBoards = () => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadBoards = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.fetchBoards(params);
      // API returns { success, data: boards[], count }; ensure we use the boards array
      const list = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      const uid = getStoredUserId();
      const visible =
        uid != null ? list.filter((b) => b && userCanViewBoard(b, uid)) : [];
      setBoards(visible);
      return visible;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error loading boards:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createBoard = useCallback(async (boardData, teamId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.createBoard(boardData, teamId);
      const newBoard = response.data;
      setBoards(prev => [newBoard, ...prev]);
      trackClientEvent(
        'board_created',
        { board_id: newBoard?.id ?? newBoard?._id ?? null, has_name: !!boardData?.name },
        { category: 'product' }
      );
      return newBoard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error creating board:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBoard = useCallback(async (boardId, updates) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.updateBoard(boardId, updates);
      const updatedBoard = response.data;
      setBoards((prev) => {
        const next = prev.map((b) => (b.id === boardId ? updatedBoard : b));
        const uid = getStoredUserId();
        return uid != null ? next.filter((b) => userCanViewBoard(b, uid)) : next;
      });
      return updatedBoard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error updating board:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteBoard = useCallback(async (boardId) => {
    setLoading(true);
    setError(null);
    
    try {
      await analyticsApi.deleteBoard(boardId);
      setBoards(prev => prev.filter(b => b.id !== boardId));
      trackClientEvent('board_deleted', { board_id: boardId }, { category: 'product' });
      return true;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error deleting board:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const duplicateBoard = useCallback(async (boardId, newName = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.duplicateBoard(boardId, newName);
      const duplicatedBoard = response.data;
      setBoards(prev => [duplicatedBoard, ...prev]);
      trackClientEvent(
        'board_duplicated',
        { source_board_id: boardId, new_board_id: duplicatedBoard?.id ?? duplicatedBoard?._id ?? null },
        { category: 'product' }
      );
      return duplicatedBoard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error duplicating board:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // When the active team changes (frontend dispatches 'teamDataUpdated'),
  // clear cached boards only. The page (e.g. ReportsPage) refetches with the new teamId.
  useEffect(() => {
    const handleTeamChange = () => {
      setBoards([]);
    };

    window.addEventListener('teamDataUpdated', handleTeamChange);
    return () => window.removeEventListener('teamDataUpdated', handleTeamChange);
  }, []);

  return {
    boards,
    loading,
    error,
    loadBoards,
    createBoard,
    updateBoard,
    deleteBoard,
    duplicateBoard
  };
};

/** True when boardId is a real DB id (not "new" or invalid) */
function isRealBoardId(boardId) {
  if (!boardId || boardId === 'new') return false;
  const n = Number(boardId);
  return Number.isInteger(n) && !Number.isNaN(n);
}

/**
 * Custom hook for managing a single board with cards
 * @param {string} boardId
 * @param {string|number|null} [teamId] - optional team context when viewing a non-default team
 */
export const useBoard = (boardId, teamId = null) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadBoard = useCallback(async (includeCards = true) => {
    if (!isRealBoardId(boardId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.fetchBoard(boardId, includeCards, teamId);
      setBoard(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error loading board:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId]);

  const updateBoard = useCallback(async (updates, options = {}) => {
    if (!isRealBoardId(boardId)) return;
    const { silent = false } = options;

    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await analyticsApi.updateBoard(boardId, updates, teamId);
      setBoard(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error updating board:', err);
      throw err;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [boardId, teamId]);

  // Load board on mount (skip when boardId is "new" or invalid)
  useEffect(() => {
    if (isRealBoardId(boardId)) {
      loadBoard();
    }
  }, [boardId, loadBoard]);

  return {
    board,
    loading,
    error,
    loadBoard,
    updateBoard
  };
};

export default useBoards;
